import { MaxFlowProblem } from '../typings';
import { MinHeap, augmentCapacityAlongPath } from './helpers';

const { min } = Math;

// Returns [
//   stepped problem,
//   finished,
//   [],
//   level graph,
//   blocking flow
// ]
export function stepDinic(
  problem: MaxFlowProblem,
  distanceLabels: number[],
  qGoldbergTarjan: number[],
  curEdge: number,
  xf: number[],
  initDone: boolean,
  qHead: number,
  qTail: number
): [
  MaxFlowProblem,
  boolean,
  number[],
  boolean[][],
  boolean[][],
  number[],
  number[],
  number,
  number[],
  boolean,
  number,
  number
] {
  // Construct level graph with double BFS
  // 1. Forward BFS for marking depth
  let q = [problem.source];
  const depth: number[] = [];
  depth[problem.source] = 1;
  let u = -1;
  let foundTargetAtDepth = Infinity;
  while (q.length > 0) {
    u = q.shift() as number;
    if (depth[u] > foundTargetAtDepth) {
      break;
    } else if (u === problem.target) {
      foundTargetAtDepth = depth[u];
    } else {
      for (const v of problem.graph.cachedTargetKeys[u]) {
        if (!depth[v] && problem.graph.edges[u][v].residualCapacity > 0) {
          depth[v] = depth[u] + 1;
          q.push(v);
        }
      }
    }
  }

  // Construct level graph with double BFS
  // 2. Backwards BFS for picking the level graph
  const visited: boolean[] = [];
  const levelEdges: boolean[][] = Array.from(problem.graph.nodes, () => []);
  let foundLevelEdge = false;
  q = [problem.target];
  while (q.length > 0) {
    u = q.shift() as number;
    if (u === problem.source) continue;
    for (const v of problem.graph.cachedTargetKeys[u]) {
      if (depth[v] + 1 === depth[u] && problem.graph.edges[v][u].residualCapacity > 0) {
        if (!visited[v]) {
          q.push(v);
          visited[v] = true;
        }
        levelEdges[v][u] = true;
        foundLevelEdge = true;
      }
    }
  }

  if (!foundLevelEdge) {
    return [problem, true, [], [], [], [], [], 0, [], false, 0, 0];
  }
  const levelEdgesCopyForVis = JSON.parse(JSON.stringify(levelEdges));

  // // Construct blocking flow
  // // using the three Indian's algorithm

  // // 1. Init throughput
  // const blockingFlow: { [key: number]: true }[] = [];
  // // T also includes values for s and t, because easier code
  // // and no difference in asymptotic complexity.
  // const T = new MinHeap<{ node: number; throughput: number }>(Object.keys(levelEdges).length);
  // const vToTIDMapping = Array.from(problem.graph.nodes, () => -1);
  // // Wastes some memory and CPU cycles, because I initialize unnecessary values,
  // // but is within asymptotic runtime requirements and easier to implement.
  // const sumInCaps = Array.from(problem.graph.nodes, () => 0);
  // const sumOutCaps = Array.from(problem.graph.nodes, () => 0);
  // for (const uAsString of Object.keys(levelEdges)) {
  //   const u = Number.parseInt(uAsString, 10);
  //   for (const vAsString of Object.keys(levelEdges[u])) {
  //     const v = Number.parseInt(vAsString, 10);
  //     sumInCaps[v] += problem.graph.edges[v][u].maxCapacity;
  //     sumOutCaps[v] += problem.graph.edges[u][v].maxCapacity;
  //   }
  // }
  // for (const uAsString of Object.keys(levelEdges)) {
  //   const u = Number.parseInt(uAsString, 10);
  //   vToTIDMapping[u] = T.insert({
  //     node: u,
  //     throughput: min(sumInCaps[u], sumOutCaps[u])
  //   });
  // }

  // // 2. Remove zero throughput nodes and edges
  // // levelEdges does not contain 0 edges

  // // 3. Init v0
  // const v0 = T.extractMinimum() as { node: number; throughput: number };

  // // 4. Init F
  // const F = Array.from(problem.graph.nodes, () => 0);
  // F[v0.node] = v0.throughput;

  // // 5. Run modified BFS
  // q = [problem.source];
  // const visited: boolean[] = [];
  // visited[problem.source] = true;
  // u = -1;
  // while (q.length > 0) {
  //   u = q.shift() as number;
  //   for (const vAsString of Object.keys(levelEdges)) {
  //     const v = Number.parseInt(vAsString, 10);
  //     if (!visited[v] && problem.graph.edges[u][v].residualCapacity > 0) {
  //       const delta = min(problem.graph.edges[u][v].residualCapacity, F[u]);
  //       problem.graph.edges[u][v].residualCapacity -= delta;
  //       F[v] += delta;
  //       F[u] -= delta;
  //       const throughput = T.getKey(vToTIDMapping[u]);
  //       throughput.throughput -= delta;
  //       T.decreaseKey(vToTIDMapping[u], throughput);
  //       visited[v] = true;
  //       q.push(v);
  //     }
  //   }
  // }

  // Construct blocking flow
  const blockingFlow: boolean[][] = Array.from(problem.graph.nodes, () => []);
  let augmentedAPath = false;
  let goneDeeper = false;
  do {
    // DFS
    let cur = problem.source;
    const path = [cur];
    const visited: boolean[] = [];
    visited[cur] = true;
    do {
      goneDeeper = false;
      for (const next of problem.graph.cachedTargetKeys[cur]) {
        if (!levelEdges[cur][next]) continue;
        if (!visited[next] && problem.graph.edges[cur][next].residualCapacity > 0) {
          path.push(next);
          cur = next;
          visited[cur] = true;
          goneDeeper = true;
          break;
        }
      }
      if (!goneDeeper) {
        const u = path.pop() as number;
        for (const v of problem.graph.cachedTargetKeys[u]) {
          levelEdges[u][v] = false;
          levelEdges[v][u] = false;
        }
        cur = path[path.length - 1];
      }
    } while (cur !== problem.target && path.length > 0);

    // Augment path
    augmentedAPath = false;
    if (path.length > 0 && path[path.length - 1] === problem.target) {
      let minResCap = Infinity;
      for (let i = 1; i < path.length; i += 1) {
        const resCap = problem.graph.edges[path[i - 1]][path[i]].residualCapacity;
        minResCap = min(minResCap, resCap);
      }
      problem = augmentCapacityAlongPath(problem, path, minResCap);

      augmentedAPath = true;
      // Add path to blocking flow
      for (let i = 1; i < path.length; i += 1) {
        const s = path[i - 1];
        const t = path[i];
        blockingFlow[s][t] = true;
      }
    }
  } while (augmentedAPath);

  return [problem, false, [], levelEdgesCopyForVis, blockingFlow, [], [], 0, [], false, 0, 0];
}
